import { useState, useEffect, useMemo } from "react";
import GenericFormTabPo from "./tabs/GenericFormTabPo";
import "bootstrap-icons/font/bootstrap-icons.css";
import { itemDetailsFields } from "./formFields.js/PurchaseOrderFields";
import { toast } from "../../components/AppToast";
import SerialCaptureModal, { SerialCaptureButton } from "../../components/SerialCaptureModal";
import { compactSerialRows, countCapturedSerials, resolveSerialColumns } from "../../utils/serialCapture";

const defaultValues = {
    lineNo: "00001",
    purchaseOrder: "",
    status: "New",
    qcRequired: "No",
    item: "",
    description: "",
    receivedQty: "",
    supplierLot: "",
    pack: "",
    lpnNo: "",
    batchNum: "",
    qcAutoAdjust: "No",
    orderedQty: "",
    uom: "EA",
    unitPrice: "",
    discount: "",
    tax: "",
    itemPOTolerance: "",
    itemCube: "",
    itemWeight: "",
    serialConfig: null,
    serialDetails: []
};

const requiredFields = ["skuId"];


const ItemDetailsForms = ({
    poFormData,
    initialForm,
    initialValues,
    onSubmit,
    onCancel,
    onAddToTable, // Add this prop
    isNextButton,
    isEditMode,
    itemNumber,
    readOnly 
}) => {
    const [formData, setFormData] = useState({
        ...defaultValues,
        ...initialForm,
        ...initialValues,
        lineNo: (initialValues && initialValues.lineNo)
            ? initialValues.lineNo
            : String(itemNumber).padStart(5, "0"),
    });
    const [errors, setErrors] = useState({});
    const [showSerialModal, setShowSerialModal] = useState(false);

    const serialEnabled =
        !!formData.serialConfig?.inboundEnabled || (formData.serialDetails?.length ?? 0) > 0;
    const serialColumns = useMemo(
        () => resolveSerialColumns(formData.serialConfig?.inboundLabels, formData.serialDetails),
        [formData.serialConfig, formData.serialDetails]
    );

    useEffect(() => {
        setFormData({
            ...defaultValues,
            ...initialForm,
            ...initialValues,
            lineNo: (initialValues && initialValues.lineNo)
                ? initialValues.lineNo
                : String(itemNumber).padStart(5, "0"),
        });
        setErrors({});
    }, [initialValues, initialForm, itemNumber]);

    const validate = () => {
        const newErrors = {};
        requiredFields.forEach(field => {
            if (!formData[field] || formData[field].toString().trim() === "") {
                newErrors[field] = "This field is required";
            }
        });
        if (formData.orderedQty && isNaN(Number(formData.orderedQty)) && formData.orderedQty < 0) {
            newErrors.orderedQty = "Must be a positive number";
        }
        
        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "lineNo") return;
        const numericFields = new Set([
            "orderedQty",
            "unitPrice",
            "discount",
            "tax",
            "itemPOTolerance",
            "itemCube",
            "itemWeight",
        ]);
        if (numericFields.has(name) && value && !/^-?\d*(\.\d*)?$/.test(value)) {
             
             return;
        }
        setFormData((prev) => ({
            ...prev,
            [name]: value,
            ...(name === "skuId" && value !== prev.skuId ? { serialConfig: null, serialDetails: [] } : {}),
        }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };
    const handleSubmit = (e) => {
        debugger
        e.preventDefault();
        if (readOnly) return;
        const validationErrors = validate();
        if (formData.orderedQty < 0) {
            const errorElement = document.querySelector(`[name="orderedQty"]`);
            if (errorElement) {
                errorElement.focus();
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                toast.error("Ordered Quantity cannot be negative");
                
            }
            return;
        }
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            
            // Focus on the first field with an error
            const firstErrorField = Object.keys(validationErrors)[0];
            const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
            if (errorElement) {
                errorElement.focus();
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }
        
        const submitData = {
            ...formData,
            serialDetails: compactSerialRows(formData.serialDetails, formData.orderedQty),
        };

        // Call onAddToTable if it exists (for adding to table)
        if (onAddToTable) {
            onAddToTable(submitData);
          
        }
        
        // Call onSubmit if it exists (for form submission)
        if (onSubmit) {
            onSubmit(submitData);
        }
        
        // Reset form after successful submission
        setFormData({
            ...defaultValues,
            purchaseOrder: poFormData?.purchaseOrder || "",
            lineNo: String((itemNumber || 1) + 1).padStart(5, "0"),
        });
        setErrors({});
    };

    const handleCancel = () => {
        if (onCancel) onCancel();
        setFormData({
            ...defaultValues,
            lineNo: String(itemNumber).padStart(5, "0"),
        });
        setErrors({});
    };
    
    return (
        <form onSubmit={handleSubmit} autoComplete="off">
            <GenericFormTabPo 
                fields={itemDetailsFields} 
                formData={formData} 
                onChange={handleChange} 
                readOnly={readOnly}
                errors={errors}
                formDataPo={poFormData}
                fieldAddons={serialEnabled ? {
                    orderedQty: (
                        <SerialCaptureButton
                            captured={countCapturedSerials(formData.serialDetails, formData.orderedQty, serialColumns)}
                            qty={formData.orderedQty}
                            onClick={() => setShowSerialModal(true)}
                        />
                    ),
                } : null}
            />
            <SerialCaptureModal
                show={showSerialModal}
                onHide={() => setShowSerialModal(false)}
                qty={formData.orderedQty}
                columns={serialColumns}
                initialRows={formData.serialDetails}
                subtitle={formData.skuId}
                readOnly={readOnly}
                onSave={(rows) => {
                    setFormData((prev) => ({ ...prev, serialDetails: rows }));
                    setShowSerialModal(false);
                }}
            />
            <div className="text-end mt-4 d-flex justify-content-end mt-5">
                <div className="ms-2">
                    {!readOnly && <button type="submit" className="btn btn-primary px-5">
                        {isEditMode ? "Update" : "Add"}
                    </button>}
                </div>
            </div>
        </form>
    );
};

export default ItemDetailsForms;